import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  inject,
  Input,
  Injector,
  NgZone,
  OnInit,
} from "@angular/core";
import { ViewerConfigurationService } from "../../../services/viewer-configuration.service";
import { ToastService } from "../../../services/internal-services/toast.service";
import { ToolbarService } from "../../../services/internal-services/toolbar.service";
import { IHistoryProject } from "../../../interfaces/i-history-project";
import { Viewerlayer } from "../../../model/viewerlayer";
import { IVisionToast } from "../../../interfaces/i-vision-toast";
import { TOAST_STATUS } from "../../../model/ToastConstants";
import { MapService } from "../../../services/internal-services/map.service";
import { GeoService } from "../../../services/internal-services/geo.service";
import { FeatureFlags } from "../../../model/feature-flags";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { VIEWER_SERVICE } from "../../../services/internal-services/viewer-services/inject-base-viewer.service";
import { MapActionsService } from "../../../../shared/services/map-actions.service";
import {
  GeofencingLayerRequest,
  GeofencingLayerResult,
  GeofencingLayerState,
  GeofencingService,
} from "../../../../shared/services/geofencing.service";
import { filter, merge, Observable, switchMap, take, tap } from "rxjs";


// Import core map tools
import SquareSelectionTool from "@vision/core-map-api/src/measures/tools/internal/SquareSelectionTool";
import PolygonSelectionTool from "@vision/core-map-api/src/measures/tools/internal/PolygonSelectionTool";
import CircleSelectionTool from "@vision/core-map-api/src/measures/tools/internal/CircleSelectionTool";
import PointSelectionTool from "@vision/core-map-api/src/measures/tools/internal/PointSelectionTool";
import VectorLayerStyles from "@vision/core-map-api/src/utils/VectorLayerStyles";
import {
  ExportConfig,
  GeofencingDialogResult,
  ImageSearchDialogResult,
  ImageSearchMethod,
  MapActionRequest,
  MapSelectionRequest,
  MapSelectionResult,
  MapSelectionType,
  SearchDisplayMode,
  SearchState,
  SearchTypeMode,
} from "../../../../compound-components/model";
import { MatDialog } from "@angular/material/dialog";
import { SearchExportDialogComponent, SearchImageDialogComponent } from "../../../../compound-components/dialog";
import { SearchQueryService } from "../../../../compound-components/services/search";
import {
  GeofencingGeometry,
  GeofencingLayerService,
  MapSearchLayerService,
} from "../../../../compound-components/services/layers";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import type { AgentDefinition } from "../../../../ai-components/chat/model/agent.types";
import type { ToolRendererConfig } from "../../../../ai-components/chat/model/tool-data.types";
import { MapInteractionService, MapEvent } from "../../../../ai-components/chat/services/map-interaction.service";
import { FeatureFlagsService } from "../../../services/internal-services/feature-flags.service";
import { AgentKitRuntimeService } from "../../../../ai-components/chat/services/agent-kit-runtime.service";
import {toolbarOptions} from "../../../../shared/model/toolbar-constants";

/**
 * Handle map viewer container and map selection workflow orchestration
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-compound-map-container",
  templateUrl: "./map-container.component.html",
  styleUrls: ["./map-container.component.scss"],
  standalone: false,
})
export class MapContainerComponent implements OnInit, AfterViewInit {
  destroyRef = inject(DestroyRef);

  viewerConfigurationService = inject(ViewerConfigurationService);

  public viewerService = inject(VIEWER_SERVICE);

  public mapService = inject(MapService);

  public toolbarService = inject(ToolbarService);

  public toastService = inject(ToastService);

  private mapActionsService = inject(MapActionsService);

  private geofencingService = inject(GeofencingService);

  private geofencingLayerService = inject(GeofencingLayerService);

  private searchQueryService = inject(SearchQueryService);

  private mapSearchLayerService = inject(MapSearchLayerService);

  flagService = inject(FeatureFlagsService);

  private dialog = inject(MatDialog);

  private ngZone = inject(NgZone);

  private injector = inject(Injector);

  // Eagerly initializes AgentKitRuntimeService at map level so the auth token is
  // bridged into CopilotKit headers before any chat component is created.
  private readonly _agentKitRuntime = inject(AgentKitRuntimeService);

  private mapInteractionService = inject(MapInteractionService);

  isToolbarOpen = true;

  projects: IHistoryProject[] = [];

  collections: Viewerlayer[] = []; // visible collection with coverage at center

  toolEnabled: string;

  showDevelopersTools = false;

  warningExpireUser = false;

  FeatureFlags = FeatureFlags;

  private pendingImageDialogConfig: any = null;

  // Map selection tool properties
  private currentSelectionRequest: MapSelectionRequest | null = null;

  private tools: Array<{ id: string; tool: any }> = [];

  resizeInfoMessage: boolean;

  /** AI agents to display in the chat sidebar and toolbar. Leave empty to hide. */
  @Input() aiAgents: AgentDefinition[] = [];

  /** Optional custom tool renderers for inline tool call display. */
  @Input() aiToolRenderers: ToolRendererConfig[] = [];

  @HostListener("window:keydown", ["$event"])
  onKeyPress($event: KeyboardEvent) {
    if ($event.altKey && $event.key === "0") {
      this.viewerService.toggleDeveloperTools();
    }
  }

  ngOnInit() {
    // Studio Viewer only ever has a single map instance; mark it as the master
    // map so BaseViewerService.updateUrlParameters() writes lat/lng/zoom to the URL.
    this.mapService.mapIndex = 0;

    this.viewerService.developerTools$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((enable: boolean) => {
      this.showDevelopersTools = enable;
    });

    this.toolbarService.enabledTool$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((enabledTool) => {
      this.toolEnabled = enabledTool === "none" ? null : enabledTool;
      this.resizeInfoMessage =
        enabledTool !== "none" &&
        enabledTool !== "measurement_tool" &&
        enabledTool !== "drawing_tool" &&
        enabledTool !== "brightness_contrast_toolbar";
    });
    this.mapService.mapInitialized$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((initialized) => {
      if (initialized) {
        // Initialize map selection tools after view is ready
        this.initializeMapSelectionTools();
      }
    });
    this.setupMapSelectionSubscriptions();
    this.setupMapActionSubscriptions();
    this.setupGeofencingSubscriptions();
    this.setupSearchPanelEventSubscriptions();
    this.setupSearchStateSubscription();
    this.setupFeatureClickSubscription();
    this.setupMapInteractionSubscriptions();
  }

  ngAfterViewInit() {
    if (this.warningExpireUser) {
      const notification: IVisionToast = {
        id: `toast-warning-expire`,
        message:
          "Your account access will expire soon. Contact " +
          this.viewerConfigurationService.getViewerConfiguration().supportEmail +
          " to extend your access",
        closeButton: true,
        timeout: 30000,
        extendedTimeOut: 0, // Needed for avoid toast hide when hover
        positionClass: "toast-top-right",
        type: TOAST_STATUS.WARNING,
      };
      this.toastService.setNotification(notification);
    }
  }

  /**
   * Setup subscriptions for all map selection requests
   */
  private setupMapSelectionSubscriptions(): void {
    // Merge all selection request observables
    merge(
      this.mapActionsService.selectionImageRequest$,
      this.mapActionsService.selectionGeofencingRequest$,
      this.mapActionsService.selectionLocationRequest$,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((request) => {
        if (request) {
          this.handleMapSelectionRequest(request);
        }
      });
  }

  private setupMapActionSubscriptions(): void {
    // Merge all selection request observables

    this.mapActionsService.mapActionRequest$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((request) => {
      if (request) {
        this.handleMapActionRequest(request);
      }
    });
  }

  private setupMapInteractionSubscriptions(): void {
    this.mapService.mapInitialized$
      .pipe(
        filter((initialized) => initialized === true),
        take(1),
        tap(() => this.registerLayerProviders()),
        switchMap(() => this.mapInteractionService.events$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.handleMapInteractionEvent(event));
  }

  /**
   * Registers a provider so the AI agent can query layer geometry by id.
   * Called once after the map is initialized.
   */
  private registerLayerProviders(): void {
    this.mapInteractionService.registerLayerGeometryProvider((layerId: string) => {
      const userLayer = this.viewerService.findUserLayerById(layerId);
      if (!userLayer) return null;

      const olLayer = this.mapService.findLayer(userLayer.id);
      if (!olLayer) return null;

      const source = olLayer.getSource?.();
      if (!source?.getFeatures) return null;

      return { geojson: GeoService.olFeaturesToGeoJson(source.getFeatures()), layer_id: userLayer.id };
    });
  }

  private handleMapInteractionEvent(event: MapEvent): void {
    switch (event.type) {
      case "move_to":
        this.mapService.setCenter([event.lon, event.lat], ViewerConstants.PROJECTIONS.WGS84);
        if (event.zoom !== undefined) this.mapService.setZoom(event.zoom);
        break;

      case "set_zoom":
        this.mapService.setZoom(event.zoom);
        break;

      case "zoom_to_extent":
        this.mapService.fitToExtent(
          [event.minLon, event.minLat, event.maxLon, event.maxLat],
          ViewerConstants.PROJECTIONS.WGS84,
        );
        break;

      case "add_data":
        // URL-based layer import — not yet implemented (pending separate task).
        break;

      case "add_geojson_layer":
        this.viewerService.addFeatureCollections([{ collection: event.collection, styleFunctionName: event.styleFunctionName, name: event.name }]);
        break;

      case "add_geotiff_layer":
        void this.viewerService.addGeoTiffLayer(event.url, event.name);
        break;

      case "add_group_layer":
        this.viewerService.addGroupLayer(event.name, event.layers);
        break;

      case "add_evidence_layer":
        this.viewerService.addEvidenceLayer({
          collection: event.collection,
          name: event.name,
          styleFunctionName: event.styleFunctionName,
          group: event.group,
        });
        break;

      case "update_evidence_layer":
        // map-layer-block-ui S5: in-place data replace + optional style. No behavior change to
        // the add case above; the primitive no-ops when the layer is missing (the card falls
        // back to ADD). One-shot persistence — the returned observable must be consumed.
        this.viewerService.updateEvidenceLayer({
          layer_id: event.layer_id,
          collection: event.collection,
          name: event.name,
          style: event.style,
          style_metadata: event.style_metadata,
        }).subscribe();
        break;
    }
  }

  /**
   * Initialize map selection tools following export-geotiff pattern
   */
  private initializeMapSelectionTools(): void {
    this.tools = [
      {
        id: "SELECTION_POINT",
        tool: new PointSelectionTool({
          map: this.mapService.getCoreMap(),
          showTooltip: false,
          areaUnits: this.viewerService.getMeasurementAreaUnit(),
          lengthUnits: this.viewerService.getMeasurementLengthUnit(),
          initCallback: this.handleSelectionInit.bind(this),
          callback: this.handleSelectionComplete.bind(this),
          interactionStyle: VectorLayerStyles.defaultStyleGeofencingInteraction,
        }),
      },
      {
        id: "SELECTION_SQUARE",
        tool: new SquareSelectionTool({
          map: this.mapService.getCoreMap(),
          showTooltip: false,
          areaUnits: this.viewerService.getMeasurementAreaUnit(),
          lengthUnits: this.viewerService.getMeasurementLengthUnit(),
          initCallback: this.handleSelectionInit.bind(this),
          callback: this.handleSelectionComplete.bind(this),
          interactionStyle: VectorLayerStyles.defaultStyleGeofencingInteraction,
        }),
      },
      {
        id: "SELECTION_POLYGON",
        tool: new PolygonSelectionTool({
          map: this.mapService.getCoreMap(),
          showTooltip: false,
          areaUnits: this.viewerService.getMeasurementAreaUnit(),
          lengthUnits: this.viewerService.getMeasurementLengthUnit(),
          initCallback: this.handleSelectionInit.bind(this),
          callback: this.handleSelectionComplete.bind(this),
          interactionStyle: VectorLayerStyles.defaultStyleGeofencingInteraction,
        }),
      },
      {
        id: "SELECTION_CIRCLE",
        tool: new CircleSelectionTool({
          map: this.mapService.getCoreMap(),
          showTooltip: false,
          areaUnits: this.viewerService.getMeasurementAreaUnit(),
          lengthUnits: this.viewerService.getMeasurementLengthUnit(),
          initCallback: this.handleSelectionInit.bind(this),
          callback: this.handleSelectionComplete.bind(this),
          interactionStyle: VectorLayerStyles.defaultStyleGeofencingInteraction,
        }),
      },
    ];

    // Register with CoreMap so hasMeasurementActive() short-circuits click handlers
    // (e.g. My Data layer clickfeatures → zoomToFeature) while a shape is being drawn.
    this.mapService.getCoreMap().addTools(this.tools.map((t) => t.tool));
  }

  /**
   * Handle incoming map selection requests
   */
  private handleMapSelectionRequest(request: MapSelectionRequest): void {
    try {
      if (request.type === null) {
        this.currentSelectionRequest = null;
        this.deactivateAllTools();
      } else {
        this.currentSelectionRequest = request;
        this.deactivateAllTools();
        this.activateSelectionTool(request.type);
      }
    } catch (error) {
      this.mapActionsService.showError(`Failed to handle selection request: ${error}`);
    }
  }

  private handleMapActionRequest(request: MapActionRequest): void {
    try {
      if (request.type === "search") {
        if (request.data.type === "fit") {
          const options = {
            padding: [20, 20, 20, 20],
            duration: 0,
          };
          if (request.data.geometry?.boundingBox) {
            this.mapService.fitToExtent(request.data.geometry.boundingBox, ViewerConstants.PROJECTIONS.WGS84, options);
          } else if (request.data.geometry?.wkt) {
            this.mapService.fitToWktPolygon(request.data.geometry.wkt, ViewerConstants.PROJECTIONS.WGS84, options);
          }
        } else if (request.data.type === "move") {
          if (request.data.coordinates) {
            this.mapService.setCenter(
              [request.data.coordinates.lng, request.data.coordinates.lat],
              ViewerConstants.PROJECTIONS.WGS84,
            );
          }
        }
      }
    } catch (error) {
      this.mapActionsService.showError(`Failed to handle selection request: ${error}`);
    }
  }

  /**
   * Activate the appropriate selection tool based on request type
   */
  private activateSelectionTool(type: MapSelectionRequest["type"]): void {
    let toolId: string;

    switch (type) {
      case MapSelectionType.POINT:
        toolId = "SELECTION_POINT";
        break;
      case MapSelectionType.SQUARE:
        toolId = "SELECTION_SQUARE";
        break;
      case MapSelectionType.POLYGON:
        toolId = "SELECTION_POLYGON";
        break;
      case MapSelectionType.CIRCLE:
        toolId = "SELECTION_CIRCLE";
        break;
      default:
        this.mapActionsService.showError(`Unknown selection type: ${type}`);
        return;
    }

    const tool = this.tools.find((t) => t.id === toolId);
    if (tool) {
      tool.tool.activate();
    } else {
      this.mapActionsService.showError(`Tool not found: ${toolId}`);
    }
  }

  /**
   * Deactivate all selection tools
   */
  private deactivateAllTools(): void {
    this.tools?.forEach((tool) => {
      tool.tool.deactivate();
      tool.tool.clear();
    });
  }

  /**
   * Deactivate all selection tools
   */
  private clearAllTools(): void {
    this.tools?.forEach((tool) => {
      tool.tool.clear();
    });
  }

  /**
   * Handle selection initialization (clear previous selection)
   */
  private handleSelectionInit(): void {
    this.clearAllTools();
  }

  /**
   * Handle selection completion and send result back
   */
  private handleSelectionComplete(result: any): void {
    this.ngZone.run(() => {
      if (!this.currentSelectionRequest) {
        this.mapActionsService.showError("No active selection request");
        return;
      }
      let geometry = result?.geometry;
      if (this.currentSelectionRequest.type === "circle" && result?.geometryCollection) {
        geometry = result.geometryCollection;
      }
      let wktGeometry: string | null = null;

      // Convert OpenLayers geometry to WKT using GeoService standard
      if (geometry) {
        try {
          GeoService.geometryTo2D(geometry);
          wktGeometry = GeoService.geometryToWKT(geometry, 20);
        } catch (error) {
          this.mapActionsService.showError(`Error converting geometry to WKT: ${error}`);
          wktGeometry = null;
        }
      }

      const selectionResult: MapSelectionResult = {
        id: this.currentSelectionRequest.id,
        type: this.currentSelectionRequest.type,
        wkt: wktGeometry,
      };

      // Send result to appropriate stream based on request ID
      this.sendSelectionResult(selectionResult);

      // Clean up
      this.deactivateAllTools();
      this.currentSelectionRequest = null;
    });
  }

  /**
   * Send selection result to the appropriate service stream
   */
  private sendSelectionResult(result: MapSelectionResult): void {
    if (result.id.includes("image-search")) {
      if (result.wkt) {
        this.previewImageData(result.wkt).subscribe((response) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            result.data = reader.result as string;
            this.mapActionsService.sendImageSelectionResult(result);
            this.handleImageSelectionResult(result);
          };
          reader.readAsDataURL(response);
          return response;
        });
        result.previewUrl = this.previewImageUrl(result.wkt);
      }
    } else if (result.id.includes("geofencing")) {
      this.mapActionsService.sendGeofencingSelectionResult(result);
    } else if (result.id.includes("location-search")) {
      this.mapActionsService.sendLocationSelectionResult(result);
      this.handleLocationSelectionResult(result);
    } else {
      this.mapActionsService.showError(`Unknown selection request source: ${result.id}`);
    }
  }

  /**
   * Handle location selection result from map
   */
  private handleLocationSelectionResult(result: MapSelectionResult): void {
    if (result.wkt) {
      // Process the selected location for search
      const coordinates = GeoService.wktToPoint(result.wkt);
      this.searchQueryService
        .performLocationSearch(coordinates.coordinates[1], coordinates.coordinates[0], true)
        .subscribe(() => {});
      this.handleSearchClearEvent();

      this.searchQueryService.setSearchTypeMode(SearchTypeMode.TEXT_SEARCH);
    }
  }

  private previewImageUrl(wkt: string): string {
    const layer = this.viewerService.getProjectStatus().centerAOI
      ? this.viewerService.getProjectStatus().centerAOI.getLayerName()
      : null;
    const collection = this.viewerService.getProjectStatus().centerAOI
      ? this.viewerService.getProjectStatus().centerAOI.getName()
      : null;
    const orderBy = collection ? collection + ",layer-asc" : null;
    const epsg = 4326;
    const width = 1024;
    const height = 1024;
    const params = {
      layer,
      wkt,
      srid: epsg,
      destinationDimensions: `${width},${height}`,
      orderBy,
      attribution: false,
    };
    const url = this.mapService.extractAdvancedOrthoImageUrl(params);
    return this.removeSessionParam(url);
  }

  private previewImageData(wkt: string): Observable<Blob> {
    const layer = this.viewerService.getProjectStatus().centerAOI
      ? this.viewerService.getProjectStatus().centerAOI.getLayerName()
      : null;
    const collection = this.viewerService.getProjectStatus().centerAOI
      ? this.viewerService.getProjectStatus().centerAOI.getName()
      : null;
    const epsg = 4326;
    const width = 1024;
    const height = 1024;

    if (this.viewerService.is3DMode()) {
      const params = {
        layer,
        wkt,
        srid: epsg,
        destinationDimensions: `${width},${height}`,
        collection,
        attribution: false,
        orientation: this.viewerService.orientationSelected,
      };
      return this.mapService.extractAdvancedOrientedImage(params);
    }

    const params = {
      layer,
      wkt,
      srid: epsg,
      destinationDimensions: `${width},${height}`,
      collection,
      attribution: false,
    };
    return this.mapService.extractAdvancedOrthoImage(params);
  }

  private removeSessionParam(url: string): string {
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.delete("session");
      return urlObj.toString();
    } catch (e) {
      // Fallback for relative URLs or invalid input
      const regex = /([?&])session=[^&]*(&)?/;
      return url.replace(regex, (match, p1, p2) => (p2 ? p1 : ""));
    }
  }

  /**
   * Setup subscriptions for search panel events (export, save)
   * Centralizes event handling to avoid duplicate logic
   */
  private setupSearchPanelEventSubscriptions(): void {
    this.searchQueryService.searchPanelEvents$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      switch (event.action) {
        case "export":
          this.handleSearchExportEvent();
          break;
        case "save":
          this.handleSearchSaveEvent();
          break;
        case "clear":
          this.handleSearchClearEvent();
          break;
        default:
          console.warn("Unknown search panel event action:", event.action);
      }
    });
  }

  private setupSearchStateSubscription(): void {
    this.searchQueryService.searchState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      this.handleSearchStateUpdate(state);
    });
    this.searchQueryService.searchTypeMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((mode) => {
      this.handleSearchTypeMode(mode);
    });
  }

  /**
   * Handle search export event by opening the export dialog
   */
  private handleSearchExportEvent(): void {
    const currentQuery = this.searchQueryService.getCurrentSearchQuery();

    if (!currentQuery) {
      console.warn("No current search query available for export");
      return;
    }
    const currentState = this.searchQueryService.getCurrentSearchState();
    const exportConfig: ExportConfig = {
      searchQuery: currentQuery,
      maxAllowedRecords: 1000,
      defaultMaxRecords: currentState.results.length || 100,
      loadedResultsCount: currentState.results.length, // New property
      availableFormats: [
        {
          id: "csv",
          label: "CSV File",
          description: "Tabular data format compatible with Excel and spreadsheet applications",
          icon: "table_chart",
          fileExtension: "csv",
          recommended: true,
        },
        {
          id: "geojson-point",
          label: "GeoJSON Points",
          description: "Geographic data with point coordinates from geometry centroids",
          icon: "place",
          fileExtension: "geojson",
        },
        {
          id: "geojson-polygon",
          label: "GeoJSON Polygons",
          description: "Geographic data preserving original geometry shapes",
          icon: "polyline",
          fileExtension: "geojson",
        },
      ],
    };

    const dialogRef = this.dialog.open(SearchExportDialogComponent, {
      data: exportConfig,
      panelClass: "medium-panel-dialog",
      injector: this.injector,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        console.log("Export completed successfully:", result);
      }
    });
  }

  /**
   * Handle search save event
   * TODO: Implement actual persistence logic in IndexedDB
   */
  private handleSearchSaveEvent(): void {
    // TODO: Implement search persistence
    // TODO: Save search criteria, filters, and metadata to IndexedDB
    // TODO: Add functionality to restore saved searches
    // TODO: Add functionality to apply saved search as fixed layer on map
    // TODO: Show user feedback about save success/failure

    // For now, just show a console message
    this.toastService.setNotification({
      id: "search-save-placeholder",
      message: "Search save functionality will be implemented in a future release",
      type: TOAST_STATUS.INFO,
      timeout: 5000,
      positionClass: "toast-top-right",
      closeButton: true,
    });
  }

  /**
   * Handle search clear event
   */
  private handleSearchClearEvent(): void {
    this.mapSearchLayerService.clearSearchLayer();
    this.deactivateAllTools();
  }

  handleSearchStateUpdate(state: SearchState) {
    if (state.scorePending) {
      // Score/color aren't ready yet: open the results panel and zoom to the results area
      // early, but leave the colored layer (which styles features by scoreColor) drawn as it is today.
      if (state.isNewSearch && state.results.length > 0) {
        this.toolbarService.enableTool(toolbarOptions.SEARCH_ENHANCED_RESULTS);
        const earlyExtent = this.mapSearchLayerService.computeResultsExtent(state.results);
        if (earlyExtent) {
          this.mapService.fitToExtent(earlyExtent, ViewerConstants.PROJECTIONS.WGS84);
        }
      }
      return;
    }

    this.mapSearchLayerService.processSearchResults(state);

    // Auto-fit to results for new searches if needed. The results panel is deliberately NOT
    // opened here: it used to pop up on its own every time results arrived, covering the map
    // right after the fit. The user opens it from its own toolbar button when they want it.
    if (state.isNewSearch && state.results.length > 0) {
      const searchExtent = this.mapSearchLayerService.getSearchExtent();
      if (searchExtent) {
        this.mapService.fitToExtent(searchExtent, ViewerConstants.PROJECTIONS.WGS84);
      }
    }
  }

  handleSearchTypeMode(mode: SearchTypeMode) {
    switch (mode) {
      case SearchTypeMode.LOCATION_SEARCH:
        this.enableLocationSearch();
        break;
      case SearchTypeMode.IMAGE_SEARCH:
        this.deactivateAllTools();
        this.enableImageSearch();
        break;
      case SearchTypeMode.TEXT_SEARCH:
        this.deactivateAllTools();
        break;
    }
  }

  /**
   * Setup subscriptions for geofencing layer requests
   */
  private setupGeofencingSubscriptions(): void {
    this.geofencingService.geofencingLayerRequest$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((request) => {
      if (request) {
        this.handleGeofencingLayerRequest(request);
      }
    });
  }

  /**
   * Build the shared geofencing state from GeofencingLayerService's actual layer
   * state, since that is the source of truth for what is rendered on the map.
   */
  private buildGeofencingState(): GeofencingLayerState {
    const layerState = this.geofencingLayerService.getState();
    return {
      layerExists: layerState.layerExists,
      layerVisible: layerState.layerVisible,
      hasGeometry: layerState.hasGeometry,
      geometryType: layerState.type,
      displayWkt: layerState.wkt ?? undefined,
    };
  }

  /**
   * Handle incoming geofencing layer requests
   */
  private handleGeofencingLayerRequest(request: GeofencingLayerRequest): void {
    let result: GeofencingLayerResult;

    try {
      switch (request.operation) {
        case "create":
        case "update":
          result = this.handleGeofencingCreateOrUpdate(request);
          break;
        case "remove":
          result = this.handleGeofencingRemove(request);
          break;
        case "toggle-visibility":
          result = this.handleGeofencingToggleVisibility(request);
          break;
        default:
          result = {
            requestId: request.id,
            success: false,
            error: `Unknown operation: ${request.operation}`,
            state: this.buildGeofencingState(),
          };
      }
    } catch (error) {
      console.error("[MapContainer] Error handling geofencing request:", error);
      result = {
        requestId: request.id,
        success: false,
        error: `Failed to process request: ${error.message}`,
        state: this.buildGeofencingState(),
      };
    }

    // Send result back through service
    this.geofencingService.sendLayerOperationResult(result);
  }

  /**
   * Handle create or update geofencing layer
   */
  private handleGeofencingCreateOrUpdate(request: GeofencingLayerRequest): GeofencingLayerResult {
    if (!request.data) {
      return {
        requestId: request.id,
        success: false,
        error: "No geofencing data provided",
        state: this.buildGeofencingState(),
      };
    }

    try {
      // Convert dialog result to geometry data for layer service
      const geometryData = this.convertDialogResultToGeometryData(request.data);

      // Create or update layer
      this.geofencingLayerService.createOrUpdateLayer(geometryData);

      return {
        requestId: request.id,
        success: true,
        state: this.buildGeofencingState(),
      };
    } catch (error) {
      console.error("[MapContainer] Error creating/updating geofencing layer:", error);
      return {
        requestId: request.id,
        success: false,
        error: error.message,
        state: this.buildGeofencingState(),
      };
    }
  }

  /**
   * Handle remove geofencing layer
   */
  private handleGeofencingRemove(request: GeofencingLayerRequest): GeofencingLayerResult {
    try {
      this.geofencingLayerService.removeFeatures();

      return {
        requestId: request.id,
        success: true,
        state: this.buildGeofencingState(),
      };
    } catch (error) {
      console.error("[MapContainer] Error removing geofencing layer:", error);
      return {
        requestId: request.id,
        success: false,
        error: error.message,
        state: this.buildGeofencingState(),
      };
    }
  }

  /**
   * Handle toggle geofencing layer visibility
   */
  private handleGeofencingToggleVisibility(request: GeofencingLayerRequest): GeofencingLayerResult {
    try {
      this.geofencingLayerService.toggleVisibility();
      return {
        requestId: request.id,
        success: true,
        state: this.buildGeofencingState(),
      };
    } catch (error) {
      console.error("[MapContainer] Error toggling geofencing visibility:", error);
      return {
        requestId: request.id,
        success: false,
        error: error.message,
        state: this.buildGeofencingState(),
      };
    }
  }

  /**
   * Convert dialog result to geometry data format expected by layer service
   */
  private convertDialogResultToGeometryData(data: GeofencingDialogResult): GeofencingGeometry {
    return {
      wkt: data.displayWkt,
      type: data.geometryType,
    };
  }

  /**
   * Enables search assistant mode
   */
  private enableLocationSearch(): void {
    // Generate unique request ID for location search
    const requestId = `location-search-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Create point selection request
    const request: MapSelectionRequest = {
      id: requestId,
      type: MapSelectionType.POINT,
      data: { context: "location-search" },
    };
    //
    this.handleMapSelectionRequest(request);
  }

  /**
   * Enables image search mode
   */
  private enableImageSearch(): void {
    const dialogRef = SearchImageDialogComponent.open(this.dialog, {
      title: "Search by Image",
      description: "Select or upload an image to find similar imagery",
      initialMethod: ImageSearchMethod.UPLOAD,
      canUseMapSelection: this.searchQueryService.getCurrentDisplayMode() === SearchDisplayMode.MAP,
    });

    // Handle dialog result
    dialogRef.afterClosed().subscribe((result) => {
      this.handleImageSearchResult(result);
    });
  }

  /**
   * Handles the result from the image search dialog
   * @param result The result from the dialog
   */
  private handleImageSearchResult(result: ImageSearchDialogResult): void {
    if (!result) {
      this.deactivateAllTools();
      this.pendingImageDialogConfig = null;
      this.mapActionsService.requestImageSelection({
        id: null, // force to remove
        type: null,
      });
      this.searchQueryService.setSearchTypeMode(SearchTypeMode.TEXT_SEARCH);
    } else if (result.success) {
      this.handleSearchClearEvent();
      this.pendingImageDialogConfig = null;
      this.mapActionsService.requestImageSelection({
        id: null, // force to remove
        type: null,
      });
      switch (result.searchMethod) {
        case ImageSearchMethod.URL:
          // Handle URL-based image search
          this.processUrlImageSearch(result.url);
          break;

        case ImageSearchMethod.UPLOAD:
        case ImageSearchMethod.WKT:
          // Handle uploaded file image search
          this.processUploadedImageSearch(result.base64Data, result.imagePreview);
          break;

        default:
          console.warn("Unknown image search method:", result.searchMethod);
      }
    } else if (result.mapSelectionRequest) {
      // User requested map selection, handle the request directly
      this.pendingImageDialogConfig = {
        title: "Search by Image",
        description: "Select or upload an image to find similar imagery",
        initialMethod: ImageSearchMethod.UPLOAD,
      };
      this.handleMapSelectionRequest(result.mapSelectionRequest);
    }
  }

  private handleImageSelectionResult(result: any): void {
    if (!result) {
      this.handleSearchClearEvent();
      this.pendingImageDialogConfig = null;
      this.mapActionsService.requestImageSelection({
        id: null, // force to remove
        type: null,
      });
    } else if (result.wkt) {
      const dialogRef = SearchImageDialogComponent.open(this.dialog, {
        ...this.pendingImageDialogConfig,
        wkt: result.wkt, // Assuming geometry can be converted to WKT
        previewUrl: result.previewUrl,
        base64Data: result.data,
        canUseMapSelection: this.searchQueryService.getCurrentDisplayMode() === SearchDisplayMode.MAP,
      });
      dialogRef.afterClosed().subscribe((rs) => {
        this.handleImageSearchResult(rs);
      });
    } else {
      // Selection was cancelled, reopen dialog without geometry
      this.reopenImageDialog();
    }
  }

  private reopenImageDialog(): void {
    if (this.pendingImageDialogConfig) {
      const dialogRef = SearchImageDialogComponent.open(this.dialog, {
        ...this.pendingImageDialogConfig,
        canUseMapSelection: this.searchQueryService.getCurrentDisplayMode() === SearchDisplayMode.MAP,
      });

      dialogRef.afterClosed().subscribe((result) => {
        this.handleImageSearchResult(result);
      });
    }
  }

  /**
   * Processes URL-based image search
   * @param url URL data from dialog
   */
  private processUrlImageSearch(url: string): void {
    if (!url) {
      console.error("Invalid URL data provided for image search");
      return;
    }

    try {
      // Use the new searchByImageUrl method from SearchQueryService
      this.searchQueryService.performImageUrlSearch(url, true).subscribe(() => {
        this.searchQueryService.setSearchTypeMode(SearchTypeMode.TEXT_SEARCH);
      });
    } catch (error) {
      console.error("Failed to initiate URL image search:", error);
    }
  }

  /**
   * Processes uploaded file image search
   * @param imageData Image data from dialog
   * @param imagePreview Data URL preview for display purposes
   */
  private processUploadedImageSearch(imageData: string, imagePreview?: string): void {
    if (!imageData) {
      console.error("Invalid image data provided for search");
      return;
    }

    try {
      // Use the new searchByImageUpload method from SearchQueryService
      this.searchQueryService.performImageDataSearch(imageData, true, imagePreview).subscribe(() => {
        this.searchQueryService.setSearchTypeMode(SearchTypeMode.TEXT_SEARCH);
      });
    } catch (error) {
      console.error("Failed to initiate image upload search:", error);
    }
  }

  /**
   * Subscribe to feature click events from the map search layer and scroll to the corresponding image.
   */
  private setupFeatureClickSubscription(): void {
    this.mapSearchLayerService.featureClick$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((feature) => {
      if (!feature) return;
      // Find the index of the clicked feature in the current search results
      const searchState = this.searchQueryService.getCurrentSearchState();
      const featureId = feature.get("id");
      const index = searchState.results.findIndex((item) => `${item.centroid[1]},${item.centroid[0]}` === featureId);
      if (index !== -1) {
        this.searchQueryService.setScrollToIndex(index);
      }
    });
  }
}
